/**
 * THE GRANDMASTER'S ATELIER
 * Global JavaScript functionality
 */

// ==========================================
// Utility Functions
// ==========================================

/**
 * Debounce function to limit function execution rate
 */
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Fetch configuration for Shopify API calls
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

// ==========================================
// Announcement Bar
// ==========================================

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoRotateInterval = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    // Defer so children are fully parsed even when script loads in <head>
    setTimeout(() => {
      this.slides = this.querySelectorAll('[data-announcement-slide]');
      this.autoRotateDelay = parseInt(this.dataset.autoRotate) || 4000;
      if (this.slides.length > 1) this.init();
    }, 0);
  }

  init() {
    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.prev());
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.next());
    this.startAutoRotate();
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== index;
      slide.setAttribute('aria-hidden', i !== index);
    });
    this.currentIndex = index;
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.showSlide(nextIndex);
    this.restartAutoRotate();
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.showSlide(prevIndex);
    this.restartAutoRotate();
  }

  startAutoRotate() {
    this.autoRotateInterval = setInterval(() => this.next(), this.autoRotateDelay);
  }

  restartAutoRotate() {
    clearInterval(this.autoRotateInterval);
    this.startAutoRotate();
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}

// ==========================================
// Header Scroll Effect
// ==========================================

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.scrollThreshold = 50;
  }

  connectedCallback() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    // Check initial state
    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const isScrolled = window.scrollY > this.scrollThreshold;

    if (isScrolled) {
      this.classList.add('site-header--scrolled');
      this.classList.remove('site-header--transparent');
    } else {
      this.classList.remove('site-header--scrolled');
      if (this.dataset.transparent === 'true') {
        this.classList.add('site-header--transparent');
      }
    }
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}

// ==========================================
// Mega Menu
// ==========================================

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.closeTimeout = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-mega-menu-trigger]');
    this.content = this.querySelector('[data-mega-menu-content]');

    if (this.trigger && this.content) {
      this.init();
    }
  }

  init() {
    // Open on trigger hover
    this.trigger.addEventListener('mouseenter', () => this.open());

    // Close when leaving trigger (with delay to allow moving to content)
    this.trigger.addEventListener('mouseleave', () => this.scheduleClose());

    // Cancel close when entering content
    this.content.addEventListener('mouseenter', () => this.cancelClose());

    // Close when leaving content
    this.content.addEventListener('mouseleave', () => this.close());

    // Also handle the wrapper element for seamless hover
    this.addEventListener('mouseenter', () => this.cancelClose());
    this.addEventListener('mouseleave', () => this.scheduleClose());
  }

  open() {
    this.cancelClose();
    // Close any other open mega menus first
    document.querySelectorAll('mega-menu').forEach(menu => {
      if (menu !== this && menu.content) {
        menu.close();
      }
    });
    this.content.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.content.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  scheduleClose() {
    this.closeTimeout = setTimeout(() => this.close(), 200);
  }

  cancelClose() {
    clearTimeout(this.closeTimeout);
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

// ==========================================
// Drawer Component
// ==========================================

class DrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.closeButtons = this.querySelectorAll('[data-drawer-close]');

    this.init();
  }

  init() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButtons.forEach(btn => btn.addEventListener('click', () => this.close()));

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.classList.add('is-open');
    requestAnimationFrame(() => { document.body.style.overflow = 'hidden'; });
    this.dispatchEvent(new CustomEvent('drawer:open'));
  }

  close() {
    this.classList.remove('is-open');
    requestAnimationFrame(() => { document.body.style.overflow = ''; });
    this.dispatchEvent(new CustomEvent('drawer:close'));
  }

  toggle() {
    this.classList.contains('is-open') ? this.close() : this.open();
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}

// ==========================================
// Cart Drawer
// ==========================================

class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    this.itemsContainer = this.querySelector('[data-cart-items]');
    this.subtotalElement = this.querySelector('[data-cart-subtotal]');
    this.countElements = document.querySelectorAll('[data-cart-count]');

    this.init();
  }

  init() {
    super.init();

    // Listen for add to cart events
    document.addEventListener('cart:add', () => this.refresh());

    // Quantity change handlers
    this.addEventListener('click', (e) => {
      const quantityBtn = e.target.closest('[data-quantity-change]');
      if (quantityBtn) {
        const key = quantityBtn.dataset.key;
        const change = parseInt(quantityBtn.dataset.quantityChange);
        this.updateQuantity(key, change);
      }

      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        const key = removeBtn.dataset.key;
        this.removeItem(key);
      }

      // Upsell add to cart handler
      const upsellBtn = e.target.closest('[data-upsell-add]');
      if (upsellBtn) {
        this.addUpsellItem(upsellBtn);
      }
    });
  }

  async addUpsellItem(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');
    const originalText = button.textContent;

    // Analytics tracking
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'upsell_add_to_cart',
        upsell_location: button.dataset.upsellLocation || 'cart_drawer',
        product_id: button.dataset.productId,
        product_title: button.dataset.productTitle,
        variant_id: variantId,
        value: parseFloat(button.dataset.productPrice || 0) / 100
      });
    }
    document.dispatchEvent(new CustomEvent('upsell:add', {
      detail: {
        variantId,
        location: button.dataset.upsellLocation || 'cart_drawer',
        productId: button.dataset.productId,
        productTitle: button.dataset.productTitle
      }
    }));

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      const result = await response.json();

      if (result.status === 422) {
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
        return;
      }

      // Show success state
      button.textContent = 'Added!';

      // Refresh cart to show new item and update upsell section
      await this.refresh();

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 1500);

    } catch (error) {
      console.error('Error adding upsell item:', error);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 2000);
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newItems = doc.querySelector('[data-cart-items]');
      const newSubtotal = doc.querySelector('[data-cart-subtotal]');
      const newUpsell = doc.querySelector('[data-upsell-section]');
      const currentUpsell = this.querySelector('[data-upsell-section]');
      const newFooter = doc.querySelector('.cart-drawer__footer');
      const currentFooter = this.querySelector('.cart-drawer__footer');

      if (newItems) this.itemsContainer.innerHTML = newItems.innerHTML;
      const currentSubtotal = this.querySelector('[data-cart-subtotal]');
      if (newSubtotal && currentSubtotal) currentSubtotal.innerHTML = newSubtotal.innerHTML;

      // Update upsell section (products may change based on cart contents)
      if (currentUpsell && newUpsell) {
        currentUpsell.innerHTML = newUpsell.innerHTML;
      } else if (currentUpsell && !newUpsell) {
        currentUpsell.remove();
      } else if (!currentUpsell && newUpsell) {
        // Insert upsell inside the scroll container (same as server render), after cart items
        const scrollContainer = this.querySelector('.cart-drawer__scroll');
        if (scrollContainer) {
          scrollContainer.appendChild(newUpsell);
        }
      }

      // Update footer visibility
      if (currentFooter && !newFooter) {
        currentFooter.remove();
      } else if (!currentFooter && newFooter) {
        this.querySelector('.cart-drawer__content').appendChild(newFooter);
      }

      this.updateCartCount();
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  async updateQuantity(key, change) {
    const lineItem = this.querySelector(`[data-line-item-key="${key}"]`);
    const currentQuantity = parseInt(lineItem?.dataset.quantity) || 1;
    const newQuantity = Math.max(0, currentQuantity + change);

    if (newQuantity === 0) {
      this.removeItem(key);
      return;
    }

    try {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: key,
          quantity: newQuantity
        })
      });

      if (response.ok) {
        this.refresh();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  async removeItem(key) {
    try {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (response.ok) {
        this.refresh();
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cart = await response.json();

      this.countElements.forEach(el => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// ==========================================
// Add to Cart Form
// ==========================================

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  async onSubmit(e) {
    e.preventDefault();

    if (this.submitButton.disabled) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');

    const formData = new FormData(this.form);
    const config = fetchConfig();

    config.body = JSON.stringify({
      items: [{
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity')) || 1
      }]
    });

    try {
      const response = await fetch(window.routes.cart_add_url, config);
      const result = await response.json();

      if (result.status === 422) {
        this.handleError(result.description);
        return;
      }

      // Dispatch event so cart count badge updates
      document.dispatchEvent(new CustomEvent('cart:add', { detail: result }));

      // Go to cart page instead of opening the drawer
      window.location.href = window.routes.cart_url;

      // Update button text temporarily
      const originalText = this.submitButton.textContent;
      this.submitButton.textContent = 'Added!';
      setTimeout(() => {
        this.submitButton.textContent = originalText;
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleError('There was an error adding to cart.');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('loading');
    }
  }

  handleError(message) {
    const errorEl = this.querySelector('[data-error-message]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

// ==========================================
// Quantity Selector
// ==========================================

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.decreaseBtn = this.querySelector('[data-decrease]');
    this.increaseBtn = this.querySelector('[data-increase]');

    if (this.input) {
      this.init();
    }
  }

  init() {
    this.decreaseBtn?.addEventListener('click', () => this.decrease());
    this.increaseBtn?.addEventListener('click', () => this.increase());
    this.input.addEventListener('change', () => this.validate());
  }

  decrease() {
    const min = parseInt(this.input.min) || 1;
    const current = parseInt(this.input.value) || 1;
    if (current > min) {
      this.input.value = current - 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  increase() {
    const max = parseInt(this.input.max) || 99;
    const current = parseInt(this.input.value) || 1;
    if (current < max) {
      this.input.value = current + 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  validate() {
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || 99;
    let value = parseInt(this.input.value) || 1;

    value = Math.max(min, Math.min(max, value));
    this.input.value = value;
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}

// ==========================================
// Mobile Menu
// ==========================================

class MobileMenu extends DrawerComponent {
  constructor() {
    super();
    this.toggleButton = document.querySelector('[data-mobile-menu-toggle]');

    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.toggle());
    }
  }

  open() {
    super.open();
    this.toggleButton?.setAttribute('aria-expanded', 'true');
  }

  close() {
    super.close();
    this.toggleButton?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}

// ==========================================
// Search Drawer
// ==========================================

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
    this.searchInput = this.querySelector('[data-search-input]');
    this.resultsContainer = this.querySelector('[data-search-results]');

    this.init();
  }

  init() {
    super.init();

    if (this.searchInput && window.routes.predictive_search_url) {
      this.searchInput.addEventListener('input', debounce(() => this.search(), 300));
    }
  }

  open() {
    super.open();
    setTimeout(() => this.searchInput?.focus(), 100);
  }

  async search() {
    const query = this.searchInput.value.trim();

    if (query.length < 2) {
      this.resultsContainer.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`
      );

      if (!response.ok) return;

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const results = doc.querySelector('[data-predictive-search-results]');

      if (results) {
        this.resultsContainer.innerHTML = results.innerHTML;
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}

// ==========================================
// Hero Slider
// ==========================================

class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('[data-slide]');
    this.dots = this.querySelectorAll('[data-dot]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.currentIndex = 0;
    this.autoPlayInterval = null;
    this.autoPlayDelay = parseInt(this.dataset.autoplay) || 6000;

    if (this.slides.length > 1) {
      this.init();
    }
  }

  init() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    // Pause on hover
    this.addEventListener('mouseenter', () => this.stopAutoPlay());
    this.addEventListener('mouseleave', () => this.startAutoPlay());

    this.startAutoPlay();
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
      slide.setAttribute('aria-hidden', i !== index);
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.showSlide(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.showSlide(prevIndex);
  }

  goTo(index) {
    this.showSlide(index);
    this.restartAutoPlay();
  }

  startAutoPlay() {
    if (this.autoPlayDelay > 0) {
      this.autoPlayInterval = setInterval(() => this.next(), this.autoPlayDelay);
    }
  }

  stopAutoPlay() {
    clearInterval(this.autoPlayInterval);
  }

  restartAutoPlay() {
    this.stopAutoPlay();
    this.startAutoPlay();
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}

// ==========================================
// Accordion
// ==========================================

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('[data-accordion-content]');

    if (this.summary) {
      this.summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    }
  }

  toggle() {
    const isOpen = this.hasAttribute('open');

    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.setAttribute('open', '');
    if (this.content) {
      requestAnimationFrame(() => {
        this.content.style.maxHeight = this.content.scrollHeight + 'px';
      });
    }
  }

  close() {
    this.removeAttribute('open');
    if (this.content) {
      this.content.style.maxHeight = '0';
    }
  }
}

if (!customElements.get('accordion-item')) {
  customElements.define('accordion-item', AccordionItem);
}

// ==========================================
// Initialize on DOM Ready
// ==========================================

document.addEventListener('DOMContentLoaded', () => {
  // Cart drawer toggle buttons
  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.toggle();
      }
    });
  });

  // Search drawer toggle buttons
  document.querySelectorAll('[data-search-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const searchDrawer = document.querySelector('search-drawer');
      if (searchDrawer) {
        searchDrawer.toggle();
      }
    });
  });

  // Account drawer toggle buttons
  document.querySelectorAll('[data-account-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const accountDrawer = document.querySelector('drawer-component[data-account-drawer]');
      if (accountDrawer) {
        accountDrawer.toggle();
      }
    });
  });
});

// ── Localization (Country / Currency) Dropdown ──────────────────────────────
(function () {
  function closeAll() {
    document.querySelectorAll('[data-localization-dropdown]').forEach(function (dd) {
      dd.hidden = true;
    });
    document.querySelectorAll('[data-localization-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  // Toggle on button click
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-localization-toggle]');
    if (btn) {
      e.stopPropagation();
      var wrapper = btn.closest('[data-localization-wrapper]');
      var dropdown = wrapper && wrapper.querySelector('[data-localization-dropdown]');
      if (!dropdown) return;
      var isOpen = !dropdown.hidden;
      closeAll();
      if (!isOpen) {
        dropdown.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // Select a country
    var option = e.target.closest('[data-country]');
    if (option) {
      var formId = option.getAttribute('data-form');
      var form = document.getElementById(formId);
      if (!form) return;
      var countryInput = form.querySelector('[name="country_code"]');
      var localeInput = form.querySelector('[name="locale_code"]');
      if (countryInput) {
        countryInput.value = option.getAttribute('data-country');
        if (localeInput && option.getAttribute('data-locale')) {
          localeInput.value = option.getAttribute('data-locale');
        }
        form.submit();
      }
      return;
    }

    // Click outside → close
    if (!e.target.closest('[data-localization-wrapper]')) {
      closeAll();
    }
  });

  // Escape key → close
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
})();

// ── Tracking: sync to cart note_attributes ───────────────────────────────────
// Writes tracking cookies (_fbp, _fbc, _ce_cid) and fbclid into Shopify cart
// note_attributes so the server-side orders/paid webhook can use them for CAPI.
(function syncTrackingToCart() {
  // Capture fbclid from URL immediately — iOS 17 Private Browsing strips it
  // from the address bar after load, but window.location.search still has it.
  var params = new URLSearchParams(window.location.search);
  var fbclid = params.get('fbclid');
  if (fbclid) sessionStorage.setItem('_fbclid', fbclid);

  // Capture UTMs for attribution context
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
    if (params.get(k)) sessionStorage.setItem(k, params.get(k));
  });

  // Build attributes from available tracking signals
  var attrs = {};
  var fbclidStored = sessionStorage.getItem('_fbclid');
  var fbpMatch = document.cookie.match(/_fbp=([^;]+)/);
  var ceCidMatch = document.cookie.match(/_ce_cid=([^;]+)/);

  if (fbclidStored) attrs['fbclid'] = fbclidStored;
  if (fbpMatch)     attrs['_fbp']   = fbpMatch[1];
  if (ceCidMatch)   attrs['_ce_cid'] = ceCidMatch[1];

  if (!Object.keys(attrs).length) return;

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: attrs }),
  }).catch(function () {});
})();
